import LightingKnowledgeLink from 'components/LightingKnowledgeLink';
import Tooltip from 'components/Tooltip';
import Icons from 'components/icons';
import RadioButton from 'components/ui/RadioButton';
import RichText from 'components/ui/RichText';
import Switch from 'components/ui/Switch';
import Text from 'components/ui/Text';
import RecommendedWithTooltip from 'compositions/RecommendedWithTooltip';
import { useTranslation } from 'hooks/useTranslation';
import { FC, useEffect, useState } from 'react';
import { selectGeneralState } from 'store/generalSlice';
import { useAppSelector } from 'store/hooks';
import type { NextArray, Option } from 'type';
import { convertToSlug } from 'utils';
import { getTooltip } from 'utils/tooltip';

export type OptionsGroupProps = {
  title: string;
  options: NextArray<Option>;
  onClick: (option: Option) => void;
  disabled?: boolean;
  switchControl?: boolean;
  onMouseOver?: (hovering: boolean) => void;
};

const OptionsGroup: FC<OptionsGroupProps> = ({
  title,
  options,
  onClick,
  disabled,
  switchControl,
  onMouseOver,
}) => {
  const [switchOnOption, setSwitchOnOption] = useState<Option | null>(null);
  const [switchOffOption, setSwitchOffOption] = useState<Option | null>(null);
  const { tooltips } = useAppSelector(selectGeneralState);
  const translation = useTranslation();

  useEffect(() => {
    if (switchControl) {
      const onOption = options.find((option) => option.slug.current === 'on');
      const offOption = options.find((option) => option.slug.current === 'off');
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      onOption && setSwitchOnOption(onOption);
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      offOption && setSwitchOffOption(offOption);
    }
  }, [options, switchControl]);

  const handleOnSwitchClick = () => {
    if (switchOnOption && switchOffOption) {
      if (switchOnOption?.selected) {
        onClick(switchOffOption);
      } else {
        onClick(switchOnOption);
      }
    }
  };

  const tooltip = getTooltip(title, tooltips);

  return (
    <div
      className={`transition border-grey-200 border-b last:border-b-0 ${
        disabled && 'pointer-events-none opacity-30'
      }`}
    >
      {!switchControl && (
        <div className="my-8">
          <Text
            bold
            size="large"
            color="blue"
            elementType="div"
            className="mb-4 flex items-center justify-between"
          >
            {title}
            {tooltip && (
              <Tooltip
                content={
                  <div className="text-sm tooltip">
                    <RichText text={tooltip.text || []} />
                    {tooltip.solutionId && (
                      <LightingKnowledgeLink
                        id={tooltip.solutionId}
                        sectionId={tooltip.sectionId}
                      >
                        {translation('general.learnMore')}
                      </LightingKnowledgeLink>
                    )}
                  </div>
                }
              >
                <Icons.Info className="!h-5 fill-blue" />
              </Tooltip>
            )}
          </Text>
          <div className="grid grid-cols-[1fr] gap-4">
            {options.map((option) => (
              <RadioButton
                selected={!disabled && option.selected}
                key={option._key}
                onClick={() => onClick(option)}
                className="w-full flex flex-row !items-center"
              >
                <>
                  {option.titleAndDesc?.description ? (
                    <div
                      className="flex flex-row items-center justify-between w-full"
                      onMouseOverCapture={() =>
                        onMouseOver && onMouseOver(true)
                      }
                      onMouseOutCapture={() =>
                        onMouseOver && onMouseOver(false)
                      }
                    >
                      <span className="flex-1">{option.title}</span>

                      <Tooltip
                        content={
                          <div className="text-sm tooltip">
                            <RichText
                              text={option.titleAndDesc?.description || []}
                            />
                          </div>
                        }
                      >
                        <Icons.Info className="!h-5 fill-blue" />
                      </Tooltip>
                    </div>
                  ) : (
                    <div
                      className="flex flex-row items-center justify-between w-full"
                      id={convertToSlug(option.title)}
                    >
                      <span className="flex-1">{option.title}</span>

                      {option.recommendedTextLocale && (
                        <RecommendedWithTooltip
                          tooltip={{ text: option.recommendedTextLocale }}
                        />
                      )}
                    </div>
                  )}
                </>
              </RadioButton>
            ))}
          </div>
        </div>
      )}

      {switchControl && (
        <div className="my-8 grid grid-cols-[1fr_auto] items-center">
          <Text bold size="large" color="blue" className="mr-4">
            {title}
          </Text>
          <Switch
            enabled={switchOnOption?.selected}
            onClick={handleOnSwitchClick}
          />
        </div>
      )}
    </div>
  );
};

export default OptionsGroup;
